import type { LucideIcon } from "lucide-react";
import { ChartNoAxesColumn, SlidersHorizontal, Stethoscope } from "lucide-react";
import type { Variant } from "./experiments";
import type { WebIntroPlanId } from "./server/personalized-promotion";

/* ---------------------------------------------------------------------------
   Offer screens, as data.

   A paywall here is a config, not a component: one renderer draws all of them
   (`paywall.tsx`) and fires the same four events for all of them, so a new offer
   is an entry in this file rather than a screen with its own hand-rolled
   tracking that will be wired up slightly differently and quietly disagree with
   the funnel it is supposed to be measured in.

   The ids are the analytics allowlist for `paywall_id` and `plan_id`. Anything
   not declared here cannot be reported.

   Prices are the Stripe sandbox catalog of 2026-08-12, entered once each: the
   period price is typed and the per-day price is divided out, because a paywall
   whose two prices are both typed is a paywall where one of them is eventually
   wrong. `PaywallPlan["id"]` is the server's own plan id, so an offer cannot
   name a plan Stripe has no coupon for.

   There is deliberately no slot for a live purchase counter or a star rating.
   The only version of that line this product could show would be invented, and
   an invented number is the one thing a medication journal cannot put on the
   screen where it asks for money.
--------------------------------------------------------------------------- */

export type PaywallPlan = {
  /** The same id `webIntroCoupons` and `webIntroPrices` are keyed by. */
  id: WebIntroPlanId;
  /** The row's own name. */
  title: string;
  /** How the billing period reads inside a sentence. */
  renewsEvery: string;
  /** Days in the period, so the per-day figure is derived rather than typed. */
  days: number;
  /** What it renews at, in dollars. */
  listPrice: number;
  /** What the first period costs once the intro coupon is applied. */
  introPrice: number;
  /** Sits on the row's top edge. Reserved for the plan the offer pushes. */
  badge?: string;
  /** The renewal terms, stated under the button while this plan is selected. */
  footnote: string;
  /** The same terms after the one-time introductory window has expired. */
  regularFootnote: string;
};

export type PaywallConfig = {
  id: string;
  headline: string;
  /** At most one line, and only where it changes the decision. */
  detail?: string;
  /** Three at most: this is the reason someone taps, not a feature list. */
  points: ReadonlyArray<readonly [LucideIcon, string]>;
  plans: readonly [PaywallPlan, ...PaywallPlan[]];
  cta: string;
  /** The quiet reassurance under the benefit lines. */
  note?: string;
  /** Which number the plan row leads with. This is the offer experiment. */
  emphasis: "period" | "per-day";
  proof: { claim: string; source: string };
  guarantee: { title: string; body: string };
  /** Who the card statement will name. */
  merchant: string;
  /** What Stripe Checkout will offer to take. Named, not drawn: a redrawn card
      logo is a trademark rendered by someone who is guessing at it. */
  payment: readonly string[];
  /** How long the personalized code is held for, in minutes. */
  holdMinutes: number;
  /** Where the button goes when checkout cannot run — no key on the deployment,
      or the endpoint answering that it is not configured. */
  destination: string;
};

export const money = (amount: number) => `$${amount.toFixed(2)}`;

/** Rounded to the cent it is written as, not floored: $19.99 over 28 days is
    71.4¢, and a plan that advertises 71¢ a day has to be the one that charges
    $19.99 — the division is the only place those two can agree. */
export const perDay = (amount: number, days: number) => Math.round((amount / days) * 100) / 100;

function plan(
  id: WebIntroPlanId,
  title: string,
  renewsEvery: string,
  days: number,
  listPrice: number,
  introPrice: number,
  badge?: string,
): PaywallPlan {
  return {
    id,
    title,
    renewsEvery,
    days,
    listPrice,
    introPrice,
    badge,
    footnote:
      `${money(introPrice)} today, then ${money(listPrice)} every ${renewsEvery} `
      + "until you cancel.",
    regularFootnote:
      `${money(listPrice)} today, then ${money(listPrice)} every ${renewsEvery} `
      + "until you cancel.",
  };
}

const plans = [
  plan("1w", "1 week", "week", 7, 13.86, 6.93),
  plan("4w", "4 weeks", "4 weeks", 28, 39.99, 19.99, "MOST POPULAR"),
  plan("12w", "12 weeks", "12 weeks", 84, 79.99, 39.99),
] as const satisfies PaywallConfig["plans"];

// The public listing is not live yet. Avoid sending a paid-funnel visitor to
// Apple's 404; switch this to the listing when App Store Connect publishes it.
const earlyAccess = "https://tryvessa.app/support";

/** The same three reasons in both arms: the offer experiment tests price
    framing, so everything that is not a price stays fixed. */
const points = [
  [ChartNoAxesColumn, "Patterns across your weeks"],
  [SlidersHorizontal, "Reminders that fit your routine"],
  [Stethoscope, "A record your prescriber can read"],
] as const satisfies PaywallConfig["points"];

const base = {
  headline: "Choose the best plan for you",
  points,
  plans,
  cta: "Get my plan",
  note: "A subscription is required to use Vessa.",
  proof: {
    claim: "People who stay three months log twice as many doses as people who stop at one.",
    source: "According to Vessa research, 2026.",
  },
  guarantee: {
    title: "Money-back guarantee",
    body:
      "If Vessa doesn't help you stay on track, we'll refund your first payment — "
      + "within 30 days, or before your first billing period ends.",
  },
  merchant: "Octalen",
  payment: ["Visa", "Mastercard", "Amex", "Apple Pay"],
  holdMinutes: 10,
  destination: earlyAccess,
} satisfies Omit<PaywallConfig, "id" | "emphasis">;

export const paywalls = {
  /** The period price leads: what the card is charged, at the size it matters. */
  focus: {
    ...base,
    id: "focus",
    emphasis: "period",
  },

  /** The same three prices, divided by the day. */
  "per-day": {
    ...base,
    id: "per-day",
    emphasis: "per-day",
  },
} as const satisfies Record<string, PaywallConfig>;

export type PaywallId = keyof typeof paywalls;

export const paywallIds = Object.keys(paywalls) as PaywallId[];

/** The experiment that chooses the offer, and the offer each arm gets. */
export const paywallExperiment = "paywall-offer" as const;

const offerByVariant: Record<Variant<typeof paywallExperiment>, PaywallId> = {
  control: "focus",
  "per-day": "per-day",
};

export function paywallForVariant(variant: Variant<typeof paywallExperiment>) {
  return paywalls[offerByVariant[variant]];
}

export function isPaywallId(value: unknown): value is PaywallId {
  return typeof value === "string" && Object.hasOwn(paywalls, value);
}

export function isPlanIdOf(paywallId: unknown, value: unknown): boolean {
  if (!isPaywallId(paywallId)) return false;
  return paywalls[paywallId].plans.some((plan) => plan.id === value);
}
